import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, Alert } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { setAuth } from '@rbac/permissions';
import { fetchDemoAccounts } from '@dreamtree-org/landing-kit/auth';
import { getApiRoot } from '@/lib/domainResolver';
import AuthShell from './auth/AuthShell';

// Email + password are verified against this app's own app_users table (config/userStore.js)
// — this app is the identity and password authority. On success the server sets an httpOnly
// JWT cookie; we then load the real profile + effective permissions via /me.
export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { fetchApi, loading } = useAppApi();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [ssoEnabled, setSsoEnabled] = useState(false);

  // SSO (enterprise OIDC) is configured server-side; the login page only learns
  // whether to show the handoff. A plain fetch — this is pre-auth by definition.
  useEffect(() => {
    fetch('/api/auth/oidc/status')
      .then((r) => r.json())
      .then((j) => setSsoEnabled(!!(j && j.enabled)))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      await fetchApi('login', { email, password });
      const me = await fetchApi('me'); // real profile + effective permissions, not the login response
      setAuth(me);
      nav((loc.state && loc.state.from) || '/', { replace: true });
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message || 'Login failed');
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to build, approve and run your AI agents."
      demoAccounts={{
        load: () => fetchDemoAccounts(`${getApiRoot()}/api/auth/demo-accounts`),
        onSelect: (a) => { setEmail(a.identifier); setPassword(a.password); setErr(''); },
      }}
    >
      <div className="space-y-4">
        {err ? <Alert variant="error" title="Sign-in failed">{err}</Alert> : null}
        <form onSubmit={submit} className="space-y-4">
          {/* The library renders its required asterisk in text-error-500 (3.76:1 on
              white, below the 4.5:1 minimum), with no prop to restyle it — so the
              asterisk is owned here in text-error-700 (~7:1). `required` only
              renders that asterisk (it never reaches the native input), which is
              why dropping the prop changes no validation behavior. NOTE: a <>
              fragment wrapper breaks this repo's espree version when it mixes text
              and elements — hence the explicit outer <span>. */}
          <Input label={<span>Email <span className="text-error-700" aria-hidden="true">*</span></span>} type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" />
          <Input label={<span>Password <span className="text-error-700" aria-hidden="true">*</span></span>} type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" />
          <Button type="submit" fullWidth size="lg" loading={loading}>Sign in</Button>
        </form>
        {ssoEnabled && (
          <a
            href="/api/auth/oidc/start"
            className="block text-center text-sm text-primary-600 hover:underline"
          >
            Sign in with your company's single sign-on
          </a>
        )}
      </div>
    </AuthShell>
  );
}
